import { useId } from 'react'

// ── NextEpLogo ──────────────────────────────────────────────────────────────
// The NextEp brand mark: two forward chevrons + a bar (»»|) — "next episode, skip
// forward." Rebuilt 2026-08-15 ("Concept A — evolved chevron") as solid filled
// wedges instead of 2.5px strokes: the old stroked mark turned to illegible
// hairlines at 16px (browser-tab favicon size), which is why five different
// surfaces had each hand-drawn their own divergent workaround instead of sharing
// this component. Same glyph the app already had recognition for, same gold
// gradient, just holds its mass at small sizes now. This is the ONE master
// vector — every context (favicon, PWA icon, email, splash) renders from this
// same path data via scripts/gen-icons.mjs, not a hand-copy.
//
// Props:
//   size   — number, icon size in px (default 22)
//   color  — CSS color for the mark (default 'var(--amber)'); a non-amber color
//            drops the gradient and renders solid (e.g. muted/monochrome contexts)
//   bg     — show background square? (default false)
//   glow   — soft bloom around the mark (default true)
//   style  — additional style overrides

export function NextEpLogo({ size = 22, color = 'var(--amber)', bg = false, glow = true, style = {} }) {
  const uid = useId().replace(/:/g, '')
  const gradId = `neGrad-${uid}`
  const glowId = `neGlow-${uid}`
  const brand  = color === 'var(--amber)'
  const fill = brand ? `url(#${gradId})` : color

  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={style}>
      <defs>
        <linearGradient id={gradId} x1="4" y1="6" x2="21" y2="18" gradientUnits="userSpaceOnUse">
          <stop offset="0%"  stopColor="#ffe291" />
          <stop offset="52%" stopColor="var(--amber)" />
          <stop offset="100%" stopColor="#f0a81c" />
        </linearGradient>
        <filter id={glowId} x="-60%" y="-60%" width="220%" height="220%">
          <feDropShadow dx="0" dy="0" stdDeviation="1.3" floodColor="var(--amber)" floodOpacity="0.6" />
        </filter>
      </defs>

      {bg && (
        <rect x="1" y="1" width="22" height="22" rx="6.5"
          fill={fill} fillOpacity="0.12"
          stroke={fill} strokeOpacity="0.9" strokeWidth="1.2" />
      )}

      <g filter={glow ? `url(#${glowId})` : undefined} fill={fill}>
        {/* Two solid chevron wedges (»») — filled polygons, not strokes, so the
            mark holds its shape down to a 16px favicon instead of thinning to
            hairlines. */}
        <polygon points="5,6.3 7.1,6.3 11.7,12 7.1,17.7 5,17.7 9.6,12" />
        <polygon points="11.4,6.3 13.5,6.3 18.1,12 13.5,17.7 11.4,17.7 16,12" />
        {/* The "next" terminus bar. */}
        <rect x="19.6" y="6.3" width="2.8" height="11.4" rx="1.4" />
      </g>
    </svg>
  )
}

// The sans 'NEXTEP' logotype that used to live here was retired 2026-10-19: the one
// wordmark is components/Wordmark.jsx (serif Next + slated EP). Pair it with <NextEpLogo/>.
